// frontend/src/pages/employees/LeaveRoster.jsx
//
// Moved out of LeaveStatTab.jsx (Task 12 amendment, 2026-09-29): a manager
// needs the full vacation roster and the stat-holiday tables, not one
// person's figures. LeavePanel shows exactly one person and falls back to
// rows[0] when employeeId is null, so it could not be reused here without
// showing an unlabelled headline number for an arbitrary employee.
//
// This is that same reviewed, manager-aware rendering carried across
// unchanged, with one adaptation: the old components took a `filters`
// object and derived `params.as_of = filters.endDate`. LeaveRoster takes
// explicit props instead.
import React from "react";
import { hours as fmtHours } from "./format";
import { useAnalytics } from "./useAnalytics";

const th = { padding: "12px 8px", fontWeight: 600, textAlign: "right" };
const thLeft = { ...th, textAlign: "left" };
const td = { padding: "10px 8px", textAlign: "right" };

const panel = {
  background: "white", borderRadius: 12, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: 24,
};

function VacationSection({ asOf }) {
  const params = {};
  if (asOf) params.as_of = asOf;
  const { data, loading, error } = useAnalytics("leave/", params);

  if (loading) return <div style={panel}>Loading…</div>;
  if (error) return <div style={{ ...panel, color: "#b91c1c" }}>{error}</div>;
  if (!data) return null;

  const rows = data.rows || [];

  return (
    <div style={panel}>
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>Vacation</h3>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
        Saskatchewan: 3 weeks (5.77% of hours) from the first year, 4 weeks
        (7.69%) after 10 years. Measured from each employee's hire
        anniversary, as at {data.as_of}.
      </div>

      {data.missing_hire_date?.length > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                      borderRadius: 8, padding: "10px 12px", marginBottom: 16,
                      fontSize: 13, color: "#92400e" }}>
          No hire date for {data.missing_hire_date.join(", ")} — no vacation
          can be calculated for them. Add it on the Settings page.
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ color: "#6b7280" }}>
          No vacation figures yet — nobody has a hire date recorded.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th style={thLeft}>Employee</th>
                <th style={thLeft}>Hired</th>
                <th style={th}>Yrs</th>
                <th style={th}>Weeks</th>
                <th style={th}>Hours in year</th>
                <th style={th}>Earned</th>
                <th style={th}>Taken</th>
                <th style={th}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 8px", fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: "10px 8px" }}>{r.hire_date}</td>
                  <td style={td}>{r.years_service}</td>
                  <td style={td}>
                    {r.below_floor ? (
                      <span title={`Contract says ${r.contract_weeks} weeks, which is below the Saskatchewan minimum of ${r.statutory_weeks}. The minimum is what has been used.`}>
                        <s style={{ color: "#9ca3af" }}>{r.contract_weeks}</s>{" "}
                        {r.effective_weeks}{" "}
                        {/* aria-hidden: this is a decorative below-floor
                            marker, not part of the cell's accessible text.
                            Left visible it turns the cell's name into
                            "2.00 4.00 warning sign", the same defect fixed
                            for the expand/collapse marker in
                            TimeCodesTab.jsx. */}
                        <span aria-hidden="true">⚠</span>
                      </span>
                    ) : r.effective_weeks}
                  </td>
                  <td style={td}>{fmtHours(r.base_hours)}</td>
                  <td style={td}>{fmtHours(r.earned)}</td>
                  <td style={td}>{fmtHours(r.taken)}</td>
                  <td style={{ ...td, fontWeight: 600,
                               color: Number(r.balance) < 0 ? "#b91c1c" : "inherit" }}>
                    {fmtHours(r.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #e5e7eb", fontWeight: 600 }}>
                <td style={{ padding: "12px 8px" }} colSpan={4}>Firm</td>
                <td style={td}>{fmtHours(data.totals.base_hours)}</td>
                <td style={td}>{fmtHours(data.totals.earned)}</td>
                <td style={td}>{fmtHours(data.totals.taken)}</td>
                <td style={td}>{fmtHours(data.totals.balance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function StatSection({ startDate, endDate }) {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const { data, loading, error } = useAnalytics("stat/", params);

  if (loading) return <div style={panel}>Loading…</div>;
  if (error) return <div style={{ ...panel, color: "#b91c1c" }}>{error}</div>;
  if (!data) return null;

  const anyManual = Number(data.totals?.manual_stat_hours || 0) > 0;

  return (
    <div style={panel}>
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>Statutory holidays</h3>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
        Saskatchewan's 10 public holidays. Credit is 5% of the hours logged in
        the 28 days before each one — logged hours only, so an earlier
        holiday's own credit is not in the base, being derived rather than
        logged. Calculated here rather than entered; no time entries are
        created.
      </div>

      {anyManual && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca",
                      borderRadius: 8, padding: "10px 12px", marginBottom: 16,
                      fontSize: 13, color: "#991b1b" }}>
          {data.totals.manual_stat_hours} hours of hand-entered STAT time found.
          Stat is calculated from the calendar, so hand-entered STAT is a second
          source of truth and the two will disagree. Remove those entries.
        </div>
      )}

      {data.off_calendar_stat?.length > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                      borderRadius: 8, padding: "10px 12px", marginBottom: 16,
                      fontSize: 13, color: "#92400e" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            STAT booked on a day that is not a Saskatchewan public holiday
          </div>
          {data.off_calendar_stat.map((r, i) => (
            <div key={i}>{r.date} — {r.name}, {r.hours} h</div>
          ))}
          <div style={{ marginTop: 4, fontStyle: "italic" }}>
            Boxing Day, Easter Monday and September 30 are not public holidays
            in Saskatchewan.
          </div>
        </div>
      )}

      {(data.holidays || []).map((h) => (
        <div key={h.date} style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {h.name} — {h.date}
            {h.observed_for && (
              <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>
                (observed for {h.observed_for})
              </span>
            )}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e5e7eb", color: "#6b7280" }}>
                <th style={thLeft}>Employee</th>
                <th style={th}>Hours in prior 28 days</th>
                <th style={th}>Stat credit</th>
                <th style={thLeft}>Worked that day</th>
              </tr>
            </thead>
            <tbody>
              {h.employees.map((e) => (
                <tr key={e.user_id} style={{ borderBottom: "1px solid #f9fafb" }}>
                  <td style={{ padding: "8px" }}>{e.name}</td>
                  <td style={td}>{fmtHours(e.base_28d)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{fmtHours(e.credit)}</td>
                  <td style={{ padding: "8px" }}>
                    {e.worked.length === 0 ? (
                      <span style={{ color: "#d1d5db" }}>—</span>
                    ) : (
                      e.worked.map((w, i) => (
                        <div key={i}>
                          {fmtHours(w.hours)} h {w.code} · {w.client}{" "}
                          <span style={{ color: "#b45309", fontWeight: 600 }}>
                            {/* aria-hidden on the arrow alone: it is
                                decoration, like the below-floor warning glyph
                                above and TimeCodesTab's expand marker, while
                                "1.5x" is real information and has to stay in
                                the accessible name. */}
                            <span aria-hidden="true">→</span> 1.5×
                          </span>
                        </div>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <td style={{ padding: "8px" }} colSpan={2}>Total</td>
                <td style={td}>{fmtHours(h.total_credit)}</td>
                <td style={{ padding: "8px" }}>
                  {Number(h.total_worked) > 0
                    ? `${fmtHours(h.total_worked)} h worked` : ""}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}

      {(data.holidays || []).length === 0 && (
        <div style={{ color: "#6b7280" }}>
          No public holidays fall in the selected date range.
        </div>
      )}
    </div>
  );
}

/**
 * The full firm leave roster: vacation balances for everyone, plus the
 * per-holiday stat credit tables. `leave/` and `stat/` are already
 * manager-aware server-side -- they return the whole roster for a manager
 * and exactly one row for everyone else -- so this component needs no
 * permission gate of its own.
 *
 * `leave/` takes `as_of` and no range; `stat/` takes `start_date`/
 * `end_date`. Sending a range to `leave/` is silently ignored and yields a
 * figure as at the wrong date, so the two are kept separate here rather
 * than sharing one params object.
 */
export default function LeaveRoster({ asOf, startDate, endDate }) {
  return (
    <div>
      <VacationSection asOf={asOf} />
      <StatSection startDate={startDate} endDate={endDate} />
    </div>
  );
}
